import React, { useEffect, useState } from 'react';
import { useApiSend } from '../../utils/httpClient';
import { login } from '../../endpoints/login';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import logo from '../../assets/PEO_InformalLogo_Color.png';
import { jwtDecode } from 'jwt-decode';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Image } from 'mui-image';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  session_id_cookie: string;
  login_cookie: string;
}

interface DecodedToken {
  sub: string;
  first_name: string;
  last_name: string;
  applications: string;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const { mutate } = useApiSend(
    login,
    (data: LoginResponse) => {
      setIsLoading(false);
      if (rememberMe) {
        localStorage.setItem('email', email);
      }
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('refresh', data.refresh_token);
      localStorage.setItem('session_id_cookie', data.session_id_cookie);
      localStorage.setItem('login_cookie', data.login_cookie);
      const decoded = jwtDecode<DecodedToken>(data.access_token);
      localStorage.setItem('user_id', decoded.sub);
      localStorage.setItem('first_name', decoded.first_name);
      localStorage.setItem('last_name', decoded.last_name);
      let apps = decoded.applications;
      localStorage.setItem('apps', apps);

      const redirectDestination = `${window.location.origin}/`;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action =
        'https://imis.peointernational.org/imislive/memberSite/SilentSignIn.aspx';

      const createHiddenInput = (
        name: string,
        value: string
      ): HTMLInputElement => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      };

      form.appendChild(
        createHiddenInput('session_id_cookie', data.session_id_cookie)
      );
      form.appendChild(createHiddenInput('login_cookie', data.login_cookie));
      form.appendChild(
        createHiddenInput('redirect_destination', redirectDestination)
      );

      document.body.appendChild(form);
      form.submit();
    },
    (error: Error) => {
      setError(error.message || 'An error occurred. Please try again later.');
      setIsLoading(false);
    }
  );

  const handleSubmit = async (e?: React.MouseEvent): Promise<void> => {
    setIsLoading(true);
    (mutate as any)({ email, password });
  };

  const handleRememberMe = (): void => {
    setRememberMe(!rememberMe);
    if (localStorage.getItem('email')) {
      localStorage.removeItem('email');
    }
  };
  let emailLS = localStorage.getItem('email');

  useEffect(() => {
    if (localStorage.getItem('email')) {
      setEmail(localStorage.getItem('email') || '');
      setRememberMe(true);
    }
  }, [emailLS]);

  const handleLinkPress = (e: React.MouseEvent): void => {
    e.preventDefault();
    window.open(
      'https://www.peointernational.org/about-peo/contact/',
      '_blank'
    );
  };

  const handleResetPassword = (e: React.MouseEvent): void => {
    window.location.href = '/forgot-password';
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Grid
      container
      direction={'column'}
      alignItems="center"
      sx={{ verticalAlign: 'middle' }}
    >
      <Grid item xs={12} sx={{ pt: 2, pb: 2 }}>
        <Image
          src={logo}
          alt="Logo"
          sx={{ maxWidth: '500px' }}
          width={'40vw'}
        />
      </Grid>
      <Grid item xs={12} sx={{ pb: 2 }}>
        <Alert severity="success" sx={{ maxWidth: '500px', width: '60vw' }}>
          <AlertTitle>Welcome</AlertTitle>
          Welcome to the new P.E.O. Hub! Please note that we are continuing to
          improve the Hub and not all features are currently available. We
          appreciate your patience as we work to improve your experience.
        </Alert>
      </Grid>
      <Grid container item xs={12} justifyContent={'center'}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px' }}>
          <TextField
            variant="standard"
            required
            fullWidth
            id="email"
            label="Email Address"
            autoFocus
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} justifyContent={'center'}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px', mt: 2 }}>
          <TextField
            variant="standard"
            required
            fullWidth
            autoComplete="current-password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            id="password"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            onKeyDown={handleKeyPress}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                color="primary"
                onChange={handleRememberMe}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label="Remember me"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid item sx={{ width: '60vw', maxWidth: '500px' }}>
          <LoadingButton
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            variant="contained"
            onClick={handleSubmit}
            sx={{ mt: 1 }}
          >
            Sign In
          </LoadingButton>
          <Grid item xs textAlign={'center'}>
            {error && (
              <Typography
                variant="overline"
                color="error"
                align="center"
                fontWeight={'bold'}
              >
                {error}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            container
            xs={12}
            paddingTop={1}
            justifyContent={'space-between'}
            sx={{ width: '50vw', maxWidth: '400px' }}
          >
            <Grid item>
              <Typography
                variant="body2"
                color="primary"
                onClick={handleResetPassword}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Forgot Password
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="body2"
                color="primary"
                onClick={handleLinkPress}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Contact Us
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Login;
